/**
 * HandoffProgress
 *
 * Full-screen overlay shown during the handoff transfer process.
 * Displays step-by-step progress with visual indicators.
 */
import React from 'react';
import { useSelector } from 'react-redux';
function getQRCodeSVG() {
    if (typeof document === 'undefined') {
        return null;
    }
    try {
        return require('qrcode.react').QRCodeSVG;
    }
    catch {
        return null;
    }
}
const QRCodeSVG = getQRCodeSVG();
const OVERLAY_STYLE = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 2000,
    background: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
};
const CARD_STYLE = {
    background: '#1e1e2e',
    borderRadius: 16,
    padding: '40px 48px',
    maxWidth: 400,
    width: '90%',
    textAlign: 'center',
    color: '#fff',
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
};
const TITLE_STYLE = {
    fontSize: 22,
    fontWeight: 700,
    marginBottom: 32,
    color: '#e0e0e0'
};
const STEPS = [
    { key: 'found', label: 'Found device' },
    { key: 'preparing', label: 'Preparing move' },
    { key: 'transferring', label: 'Connecting to room' },
    { key: 'establishing', label: 'Establishing video' }
];
function getStageIndex(stage) {
    const order = ['scanning', 'found', 'preparing', 'transferring', 'establishing', 'completed'];
    const idx = order.indexOf(stage);
    return idx === -1 ? 0 : idx;
}
const StepIndicator = ({ stepKey, currentStage, index }) => {
    const currentIdx = getStageIndex(currentStage);
    const stepIdx = index + 1; // offset by 1 since 'scanning' is index 0
    const isDone = currentIdx > stepIdx;
    const isCurrent = currentIdx === stepIdx;
    return (React.createElement("div", { style: {
            display: 'flex',
            alignItems: 'center',
            marginBottom: 16,
            opacity: isDone || isCurrent ? 1 : 0.4
        } },
        React.createElement("div", { style: {
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: isDone ? '#4caf50' : isCurrent ? '#2196f3' : '#444',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                fontWeight: 700,
                marginRight: 14,
                flexShrink: 0
            } }, isDone ? '\u2713' : index + 1),
        React.createElement("span", { style: {
                fontSize: 15,
                fontWeight: isCurrent ? 600 : 400
            } }, stepKey),
        isCurrent && (React.createElement("div", { style: {
                marginLeft: 8,
                width: 14,
                height: 14,
                border: '2px solid #2196f3',
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
            } }))));
};
const HandoffProgress = () => {
    const handoffInProgress = useSelector((state) => state['features/device-handoff']?.handoffInProgress || false);
    const progress = useSelector((state) => state['features/device-handoff']?.progress);
    if (!handoffInProgress || !progress) {
        return null;
    }
    const stage = progress.stage;
    if (stage === 'completed' || stage === 'failed') {
        return null;
    }
    return (React.createElement("div", { style: OVERLAY_STYLE },
        React.createElement("div", { style: CARD_STYLE },
            React.createElement("div", { style: TITLE_STYLE }, "Moving call\u2026"),
            React.createElement("div", { style: { textAlign: 'left', marginBottom: 24 } }, STEPS.map((step, idx) => (React.createElement(StepIndicator, { currentStage: stage, index: idx, key: step.key, stepKey: step.label })))),
            React.createElement("div", { style: {
                    fontSize: 13,
                    color: 'rgba(255,255,255,0.5)',
                    marginTop: 16
                } }, progress.message || 'Please wait...'),
            progress.qrUrl && (React.createElement("div", { style: {
                    background: 'rgba(255,255,255,0.08)',
                    borderRadius: 8,
                    color: '#fff',
                    fontSize: 12,
                    lineHeight: 1.4,
                    marginTop: 16,
                    overflowWrap: 'anywhere',
                    padding: 12
                } },
                QRCodeSVG && (React.createElement("div", { style: { background: '#fff', borderRadius: 8, display: 'inline-flex', padding: 8 } },
                    React.createElement(QRCodeSVG, { bgColor: '#ffffff', fgColor: '#111111', level: 'M', size: 144, value: progress.qrUrl }))),
                progress.qrUrl)),
            React.createElement("div", { style: {
                    fontSize: 12,
                    color: 'rgba(255,255,255,0.3)',
                    marginTop: 8
                } }, "Do not close this app.")),
        React.createElement("style", null, `
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `)));
};
export default HandoffProgress;
